import { AxiosError } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { put, call, takeLatest } from 'redux-saga/effects';

import {
  getDeviceSharingRequests,
  postDeviceSharing,
  filterDeviceSharingRequests,
  postDeviceSharingAction as apiPostDeviceSharingAction
} from '../api';

import {
  postDeviceSharingRequest,
  postDeviceSharingSuccess,
  postDeviceSharingFailure,
  getDeviceSharingRequestsRequest,
  getDeviceSharingRequestsSuccess,
  getDeviceSharingRequestsFailure,
  filterDeviceSharingRequestsRequest,
  filterDeviceSharingRequestsSuccess,
  filterDeviceSharingRequestsFailure,
  postDeviceSharingAction,
  postDeviceSharingActionSuccess,
  postDeviceSharingActionFailure
} from '../features/shared-access/shared-access-slice';

import {
  PostDeviceSharingRequestPayload,
  PostDeviceSharingRequestResponse,
  GetDeviceSharingRequestsResponse,
  PostDeviceSharingActionPayload,
  PostDeviceSharingActionResponse
} from '../types/devices';

import { unmarshalError } from '../utils/common';

function* handlePostDeviceSharingRequest(
  action: PayloadAction<PostDeviceSharingRequestPayload>
) {
  try {
    const response: PostDeviceSharingRequestResponse = yield call(
      postDeviceSharing,
      action.payload
    );
    if (response.statusCode === 200) {
      yield put(
        postDeviceSharingFailure({ message: 'already-exists', statusCode: 200 })
      );
      return;
    }
    yield put(postDeviceSharingSuccess(response));
  } catch (error) {
    const axiosError = error as AxiosError & { trace_id?: string };
    const message =
      'api-errors.shared-access.post-device-sharing.generic-error';
    const errorRet = unmarshalError(message, axiosError);
    if (axiosError?.trace_id) {
      errorRet.trace_id = axiosError.trace_id;
    }
    yield put(postDeviceSharingFailure(errorRet));
  }
}

function* handleGetDeviceSharingRequests() {
  try {
    const response: GetDeviceSharingRequestsResponse = yield call(
      getDeviceSharingRequests
    );
    yield put(getDeviceSharingRequestsSuccess(response));
  } catch (error) {
    const axiosError = error as AxiosError;
    const message =
      'api-errors.shared-access.get-device-sharing-requests.generic-error';
    const errorRet = unmarshalError(message);
    errorRet.statusCode = axiosError.response?.status;
    yield put(getDeviceSharingRequestsFailure(errorRet));
  }
}

function* handleFilterDeviceSharingRequests() {
  try {
    const response: GetDeviceSharingRequestsResponse = yield call(
      filterDeviceSharingRequests
    );
    yield put(filterDeviceSharingRequestsSuccess(response));
  } catch (error) {
    const axiosError = error as AxiosError;
    const message =
      'api-errors.shared-access.get-received-sharing-requests.generic-error';
    const errorRet = unmarshalError(message);
    errorRet.statusCode = axiosError.response?.status;
    yield put(filterDeviceSharingRequestsFailure(errorRet));
  }
}

function* handlePostDeviceSharingAction(
  action: PayloadAction<PostDeviceSharingActionPayload>
): Generator {
  try {
    const response = (yield call(
      apiPostDeviceSharingAction,
      action.payload
    )) as PostDeviceSharingActionResponse;
    yield put(postDeviceSharingActionSuccess(response));
  } catch (error) {
    const axiosError = error as AxiosError;
    const message =
      'api-errors.shared-access.post-device-sharing-action.generic-error';
    const errorRet = unmarshalError(message, axiosError);
    yield put(postDeviceSharingActionFailure(errorRet));
  }
}

function* SharedAccessSaga(): Generator {
  yield takeLatest(
    postDeviceSharingRequest.type,
    handlePostDeviceSharingRequest
  );
  yield takeLatest(
    getDeviceSharingRequestsRequest.type,
    handleGetDeviceSharingRequests
  );
  yield takeLatest(
    filterDeviceSharingRequestsRequest.type,
    handleFilterDeviceSharingRequests
  );
  yield takeLatest(postDeviceSharingAction.type, handlePostDeviceSharingAction);
}

export default SharedAccessSaga;
